import { Box, Stack, Tooltip } from '@mui/material'
import { green, grey, orange, red } from '@mui/material/colors'
import { type FC } from 'react'

interface VerdictBarProps {
    yes: number
    partial: number
    no: number
}

const SEGMENTS: Array<{
    key: 'yes' | 'partial' | 'no'
    label: string
    color: string
}> = [
    { key: 'yes', label: 'Yes', color: green[400] },
    { key: 'partial', label: 'Partial', color: orange[400] },
    { key: 'no', label: 'No', color: red[400] },
]

// Stacked horizontal bar that visualises the Yes / Partial / No
// verdict ratio in a single glance. Replaces the three inline pills
// on the Overview card - same data, but health reads instantly from
// the color weighting instead of requiring the eye to compare three
// numbers. A tooltip on each segment preserves the exact count.
export const VerdictBar: FC<VerdictBarProps> = ({ yes, partial, no }) => {
    const counts: Record<'yes' | 'partial' | 'no', number> = {
        yes,
        partial,
        no,
    }
    const total = yes + partial + no

    return (
        <Stack spacing={0.75}>
            <Box
                sx={{
                    display: 'flex',
                    height: 10,
                    borderRadius: 999,
                    overflow: 'hidden',
                    bgcolor: grey[200],
                }}
                data-testid="bot-audit-verdict-bar"
            >
                {total > 0
                    ? SEGMENTS.map((seg) => {
                          const count = counts[seg.key]
                          if (count === 0) return null
                          return (
                              <Tooltip
                                  key={seg.key}
                                  title={`${seg.label}: ${count}`}
                                  arrow
                              >
                                  <Box
                                      sx={{
                                          flexGrow: count,
                                          bgcolor: seg.color,
                                      }}
                                      data-testid={`bot-audit-verdict-bar-${seg.key}`}
                                  />
                              </Tooltip>
                          )
                      })
                    : null}
            </Box>
            <Stack
                direction="row"
                spacing={1.25}
                sx={{ fontSize: '0.8rem', lineHeight: 1 }}
            >
                {SEGMENTS.map((seg) => (
                    <Stack
                        key={seg.key}
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                    >
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: seg.color,
                            }}
                        />
                        <Box
                            component="span"
                            sx={{
                                fontWeight: 600,
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {counts[seg.key]}
                        </Box>
                        <Box
                            component="span"
                            sx={{
                                color: 'text.secondary',
                                fontSize: '0.7rem',
                                textTransform: 'lowercase',
                            }}
                        >
                            {seg.label}
                        </Box>
                    </Stack>
                ))}
            </Stack>
        </Stack>
    )
}
